import { store } from '@store';
import { makeObservable, override } from 'mobx';

import { Sex } from 'chaika-enums';

import { entryTypeService } from '@template/services/entryType/entryTypeService';
import { BlockFieldParams } from '@components/EntryTypeElement/Block';
import { assertIsDefined } from '@utils/assertIsDefined';

import { AbstractField } from './AbstractField';

export interface NestedEntryTypeFieldValue {
    typeId: string;
    version: number;
}

export class NestedEntryTypeField extends AbstractField<NestedEntryTypeFieldValue[], NestedEntryTypeFieldValue[]> {
    protected defaultNullValue = [];

    constructor(params: NestedEntryTypeFieldValue[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue && this.fieldValue.length > 0;
    }

    public makeUpdateParams(): NestedEntryTypeFieldValue[] {
        return this.fieldValue;
    }

    public getSectionIds({ typeId, version }: NestedEntryTypeFieldValue): string[] {
        const {
            entryTypeList,
            ehr: { sex },
        } = store;

        const entryType = entryTypeList.getVersion(typeId, version);

        const sections = sex && sex === Sex.Male ? entryType?.maleSections : entryType?.sections;

        return sections?.map(({ id }) => id) ?? [];
    }

    public getBlockIds({ typeId, version }: NestedEntryTypeFieldValue, sectionId: string): string[] {
        const { entryTypeList, entry } = store;

        const entryType = entryTypeList.getVersion(typeId, version);
        assertIsDefined(entryType, `Nested entry type ${typeId} v${version} must be loaded before its blocks are read`);

        const section = entryType.getSection(sectionId);

        const viewMode = entry.isViewMode || entry.isSigned;

        const filteredBlock = section
            ? section.blocks.filter(({ fields }) =>
                  fields.some(
                      ({ name }) =>
                          !entry.type.getFieldByName(name) && (viewMode ? entry.fields.get(name)?.hasData : true),
                  ),
              )
            : [];

        return filteredBlock.map(({ id }) => id);
    }

    public getFieldParams(
        { typeId, version }: NestedEntryTypeFieldValue,
        sectionId: string,
        blockId: string,
    ): BlockFieldParams[] {
        const { entryTypeList, entry } = store;

        const entryType = entryTypeList.getVersion(typeId, version);
        assertIsDefined(entryType, `Nested entry type ${typeId} v${version} must be loaded before its fields are read`);

        const block = entryType.getBlock(sectionId, blockId);

        const fields = entry.isViewMode ? block && block.fieldsWithData : block && block.fields;

        return fields
            ? fields
                  .filter(({ name }) => !entry.type.getFieldByName(name))
                  .map(filed => ({
                      name: filed.name,
                      width: filed.width,
                      customWidth: entryTypeService.isCustomWidthField(filed.type),
                  }))
            : [];
    }

    protected setDefaultValueValue() {
        this.defaultNullValue = [];
    }

    protected setFieldValue(value: NestedEntryTypeFieldValue[]): void {
        this.fieldValue = value;
    }
}
